import { Button, Checkbox, Group, Stack, Text } from '@mantine/core';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Activity } from '@/api/generated/models';
import { useListActivities } from '@/api/generated/activities/activities';
import {
  getGetWeekReportQueryKey,
  getListHoursQueryKey,
  getListWeekReportLinesQueryKey,
  useAddWeekReportActivity
} from '@/api/generated/week-reports/week-reports';
import { getWorkPackage } from '@/api/generated/work-structure/work-structure';
import {
  FormModal,
  ModalFooter,
  MutationButton,
  TABLE_WEEK_COL_WIDTH,
  TextField
} from '@/components/common';
import { formatWeekLabel } from '@/features/week-reports/weekReportDisplay';
import { invalidateActivityQueries } from '@/utils/invalidateActivityQueries';

interface AddActivityToWeekReportModalProps {
  opened: boolean;
  onClose: () => void;
  weekReportId: string;
  contractId: string;
  weekNumber: number;
  reportYear: number;
}

export const AddActivityToWeekReportModal = ({
  opened,
  onClose,
  weekReportId,
  contractId,
  weekNumber,
  reportYear
}: AddActivityToWeekReportModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isAdding, setIsAdding] = useState(false);

  const { data: activitiesData } = useListActivities(
    { contractId },
    { query: { enabled: opened && !!contractId } }
  );
  const eligible: Activity[] = (activitiesData ?? []).filter(
    (activity) =>
      !activity.weekReportId &&
      activity.weekNumber === weekNumber &&
      activity.reportYear === reportYear
  );

  const workPackageIds = [
    ...new Set(eligible.map((a) => a.workPackageId).filter((v): v is string => !!v))
  ];
  const workPackageQueries = useQueries({
    queries: workPackageIds.map((wpId) => ({
      queryKey: ['work-package', wpId],
      queryFn: () => getWorkPackage(wpId),
      enabled: !!wpId
    }))
  });
  const workPackageMap = new Map(
    workPackageIds.map((wpId, i) => [
      wpId,
      workPackageQueries[i]?.data as { name?: string } | undefined
    ])
  );

  const query = search.trim().toLowerCase();
  const filteredRows = eligible.filter((activity) => {
    if (!query) return true;
    const name = activity.workPackageId
      ? (workPackageMap.get(activity.workPackageId)?.name ?? '')
      : t('activities.directFromContract');
    return name.toLowerCase().includes(query);
  });

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const reset = () => {
    setSearch('');
    setSelected(new Set());
  };

  const addActivityMutation = useAddWeekReportActivity();

  // Namjerno sekvencijalno (isti obrazac kao ActivityItemPickerModal) — svaka
  // dodana aktivnost odmah ponovno agregira cijeli izvještaj na backendu.
  const handleAdd = async () => {
    setIsAdding(true);
    try {
      for (const activityId of selected) {
        await addActivityMutation.mutateAsync({ id: weekReportId, data: { activityId } });
      }
      queryClient.invalidateQueries({ queryKey: getGetWeekReportQueryKey(weekReportId) });
      queryClient.invalidateQueries({ queryKey: getListWeekReportLinesQueryKey(weekReportId) });
      queryClient.invalidateQueries({ queryKey: getListHoursQueryKey(weekReportId) });
      invalidateActivityQueries(queryClient);
      reset();
      onClose();
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <FormModal
      opened={opened}
      onClose={() => {
        reset();
        onClose();
      }}
      size={760}
      title={
        <Text fz={20} fw={700} style={{ color: 'var(--color-text-primary)' }}>
          {t('weekReportDetail.addActivityModal.title')}
        </Text>
      }
      styles={{
        content: { backgroundColor: 'var(--color-bg-surface)' },
        header: {
          backgroundColor: 'var(--color-bg-surface)',
          borderBottom: '1px solid var(--color-border-soft)'
        },
        body: { padding: 20 }
      }}
    >
      <Stack gap={16}>
        <TextField
          hideLabel
          placeholder={t('weekReportDetail.addActivityModal.searchPlaceholder')}
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
        />

        <Stack gap={0} style={{ maxHeight: 320, overflowY: 'auto' }}>
          {filteredRows.length > 0 && (
            <Group
              gap={8}
              wrap="nowrap"
              py={4}
              style={{ borderBottom: '1px solid var(--color-border-soft)' }}
            >
              <Checkbox
                readOnly
                checked={false}
                tabIndex={-1}
                style={{ visibility: 'hidden' }}
                aria-hidden
              />
              <Text
                fz={11}
                fw={600}
                w={TABLE_WEEK_COL_WIDTH}
                style={{ color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}
              >
                {t('activities.colWeek')}
              </Text>
              <Text fz={11} fw={600} style={{ flex: 1, color: 'var(--color-text-muted)' }}>
                {t('activities.colWorkPackage')}
              </Text>
            </Group>
          )}

          {filteredRows.length === 0 && (
            <Text fz={13} py={24} ta="center" style={{ color: 'var(--color-text-muted)' }}>
              {t('weekReportDetail.addActivityModal.emptyState')}
            </Text>
          )}

          {filteredRows.map((activity, index) => (
            <Group
              key={activity.id}
              gap={8}
              wrap="nowrap"
              py={10}
              role="button"
              tabIndex={0}
              onClick={() => activity.id && toggle(activity.id)}
              onKeyDown={(event) => {
                if ((event.key === 'Enter' || event.key === ' ') && activity.id)
                  toggle(activity.id);
              }}
              style={{
                cursor: 'pointer',
                borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
              }}
            >
              <Checkbox
                checked={!!activity.id && selected.has(activity.id)}
                onChange={() => activity.id && toggle(activity.id)}
                onClick={(event) => event.stopPropagation()}
              />
              <Text
                fz={13}
                w={TABLE_WEEK_COL_WIDTH}
                style={{ color: 'var(--color-text-secondary)' }}
              >
                {formatWeekLabel(activity.weekNumber, activity.reportYear)}
              </Text>
              <Text fz={13} fw={600} style={{ flex: 1, color: 'var(--color-text-primary)' }}>
                {activity.workPackageId
                  ? (workPackageMap.get(activity.workPackageId)?.name ?? '—')
                  : t('activities.directFromContract')}
              </Text>
            </Group>
          ))}
        </Stack>

        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            {t('common.cancel')}
          </Button>
          <MutationButton isPending={isAdding} disabled={selected.size === 0} onClick={handleAdd}>
            {t('weekReportDetail.addActivityModal.addSelected', { count: selected.size })}
          </MutationButton>
        </ModalFooter>
      </Stack>
    </FormModal>
  );
};
